import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import PropTypes from "prop-types";
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

/**
 * COMPONENT 1: Gap Analysis Bar Chart
 */
export const GapAnalysisChart = ({ labels = [], gapData = [], employeeName = "", t }) => {
  const barData = {
    labels,
    datasets: [
      {
        label: t("user.trainings.gap"),
        data: gapData,
        backgroundColor: gapData.map((g) => (g >= 0 ? "#108092" : "#ef5350")),
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 3, bottom: 3, left: 5, right: 5 } },
    plugins: {
      title: {
        display: true,
        text: employeeName,
        font: { size: 14, weight: "bold", family: "Arial" },
        color: "#334155",
        padding: { top: 4, bottom: 8 },
      },
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw ?? 0;
            const status = value >= 0 ? "user.trainings.aboveTarget" : "user.trainings.belowTarget";
            const icon = value >= 0 ? "▲" : "▼";
            return [
              `${t("user.trainings.chartsY")}: ${value}`,
              `${icon} ${t("user.myScorecard.status")}: ${t(status)}`,
            ];
          },
        },
      },
    },
    scales: {
      x: { ticks: { autoSkip: true, maxRotation: 0, minRotation: 0, display: false } },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          callback: (val) => val,
          color: "#374151",
          font: { size: 12, weight: "bold", family: "Arial" },
        },
        grid: { color: "#e0e0e0", borderDash: [4, 4] },
      },
    },
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", md: "50%" },
        boxSizing: "border-box",
        pr: { md: 1 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="body2" fontWeight="bold" sx={{ color: "#1f2937", mb: 0.5 }}>
        {t("user.trainings.gapTitle")}
      </Typography>

      <Box sx={{ width: "100%", height: 140, position: "relative" }}>
        {gapData && gapData.length > 0 ? (
          <Bar data={barData} options={barOptions} />
        ) : (
          <Typography variant="body2" align="center" sx={{ mt: 5, color: "#94a3b8" }}>
            {t("user.trainings.noData")}
          </Typography>
        )}
      </Box>

      <Box
        display="flex"
        justifyContent="space-around"
        alignItems="center"
        mt={0.5}
        sx={{ flexWrap: "nowrap", overflowX: "auto", minHeight: 4 }}
      >
        {labels.map((label, i) => (
          <Tooltip title={label} key={i}>
            <Typography variant="caption" noWrap sx={{ fontWeight: 600, color: "#334155" }}>
              {label?.length > 7 ? label?.slice(0, 7) + "…" : label}
            </Typography>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

GapAnalysisChart.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  gapData: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]))
    .isRequired,
  employeeName: PropTypes.string,
  t: PropTypes.func.isRequired,
};

/**
 * COMPONENT 2: Progress Chart
 */

export const ProgressChart = ({ finishedTrainings, totalTrainings, t }) => {
  const progressPercent = totalTrainings
    ? Math.round((finishedTrainings / totalTrainings) * 100)
    : 0;

  const progressColor =
    progressPercent <= 0
      ? "#d1d5db"
      : progressPercent < 50
        ? "#3b82f6"
        : progressPercent < 80
          ? "#facc15"
          : "#16a34a";

  return (
    <Box
      sx={{
        width: { xs: "100%", md: "50%" },
        boxSizing: "border-box",
        pl: { md: 1 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5, color: "#1f2937" }}>
        {t("user.trainings.landDProgress")}
      </Typography>

      <Box display="flex" alignItems="baseline" gap={1}>
        <Typography variant="body1" fontWeight={600} sx={{ fontFamily: "Arial", color: "#334155" }}>
          {finishedTrainings}
        </Typography>
        <Typography variant="body1" fontWeight={600} sx={{ fontFamily: "Arial", color: "#334155" }}>
          / {totalTrainings} {t("user.trainings.trainingsFilled")}
        </Typography>
      </Box>

      <Typography mt={1} color="primary" fontWeight={600}>
        {`${progressPercent}% ${
          progressPercent > 0
            ? t("user.trainings.trainingsCompleted")
            : t("user.trainings.notStarted")
        }`}
      </Typography>

      <Box
        mt={1}
        sx={{
          width: "100%",
          height: 25,
          borderRadius: 0,
          backgroundColor: "#e5e7eb",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${progressPercent}%`,
            height: "100%",
            backgroundColor: progressColor,
            transition: "width 0.3s ease",
            borderRadius: 0,
          }}
        />
      </Box>
    </Box>
  );
};

ProgressChart.propTypes = {
  finishedTrainings: PropTypes.number.isRequired,
  totalTrainings: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
};
